import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

// Who is signed in, which roles they hold, and which ONE role this sign-in is
// acting as — read from my_context() (supabase/migrations/0006). This replaces
// the old pattern of each page reading `profiles` directly, which the browser
// can't reach (the cause of "platform admin shown as teacher").
//
// The database enforces the active role on every request; these helpers only
// decide which screen to show.

export type RoleKey = 'platform_admin' | 'school_admin' | 'head_of_school' | 'counsellor' | 'teacher' | 'municipality_coordinator';

export type RoleGrant = {
  grant_id: string;
  role: RoleKey;
  is_primary: boolean;
  school_id: string | null;
  school_name: string | null;
  school_code: string | null;
  school_type: 'sandbox' | 'customer' | null;
  school_state: string | null;
  /** Municipality coordinators only (0019). */
  municipality_code?: string | null;
  municipality_name?: string | null;
};

export type StaffContext = {
  status: 'ok';
  user_id: string;
  display_name: string;
  is_test: boolean;
  test_expires_at: string | null;
  roles: RoleGrant[];
  active_grant_id: string | null;
  active_role: RoleKey | null;
  active_school_id: string | null;
  mfa_required: boolean;
  /** This role signs in with two-factor (platform admins; school roles in Customer schools — 0015). */
  mfa_applies: boolean;
  /** A test account still on its Zencera-issued password: no role works until it's replaced (0011). */
  must_set_password: boolean;
  /** The staff notice (what is stored about you) is waiting to be read (0019). */
  notice_required?: boolean;
  notice_version?: number;
  unread_notifications: number;
};

export type ContextResult =
  | { kind: 'ok'; ctx: StaffContext }
  | { kind: 'signed_out' }
  | { kind: 'no_access' }
  | { kind: 'unavailable'; message: string };

export async function fetchContext(): Promise<ContextResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return { kind: 'signed_out' };

  const { data, error } = await supabase.rpc('my_context');
  if (error) return { kind: 'unavailable', message: error.message };
  const body = data as { status?: string } | null;
  if (!body || body.status === 'no_access') return { kind: 'no_access' };
  return { kind: 'ok', ctx: data as StaffContext };
}

export async function activateRole(grantId: string): Promise<ContextResult> {
  const { data, error } = await supabase.rpc('set_active_role', { p_grant_id: grantId });
  if (error) return { kind: 'unavailable', message: error.message };
  return { kind: 'ok', ctx: data as StaffContext };
}

// Each role's home screen. Roles whose own screens are built in later steps
// land on /home for now.
export function homeFor(role: RoleKey | null): string {
  switch (role) {
    case 'platform_admin':
      return '/admin';
    case 'head_of_school':
      return '/school';
    case 'teacher':
      return '/teacher';
    case 'counsellor':
      return '/counsellor';
    case 'school_admin':
      return '/home';
    case 'municipality_coordinator':
      return '/municipality';
    default:
      return '/choose-role';
  }
}

// Where a freshly loaded context should send the person.
export function nextStep(ctx: StaffContext): string {
  if (ctx.must_set_password) return '/set-password';
  if (ctx.mfa_required) return '/mfa';
  if (!ctx.active_role) return ctx.roles.length === 0 ? '/no-roles' : '/choose-role';
  if (ctx.notice_required) return '/notice';
  return homeFor(ctx.active_role);
}

export function activeGrant(ctx: StaffContext): RoleGrant | undefined {
  return ctx.roles.find((r) => r.grant_id === ctx.active_grant_id);
}

/**
 * Page gate: loads the context and redirects unless the active role is one of
 * `allowed`. Returns the context once the page may render.
 */
export function useStaffGate(allowed: RoleKey[]): { ctx: StaffContext | null; error: string | null } {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState<StaffContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = allowed.join(',');

  useEffect(() => {
    let cancelled = false;
    fetchContext().then((res) => {
      if (cancelled) return;
      if (res.kind === 'signed_out') {
        navigate('/teacher/login', { replace: true });
      } else if (res.kind === 'no_access') {
        supabase.auth.signOut().finally(() => navigate('/teacher/login?reason=no_access', { replace: true }));
      } else if (res.kind === 'unavailable') {
        setError(res.message);
      } else {
        const step = nextStep(res.ctx);
        if (res.ctx.active_role && !res.ctx.mfa_required && !res.ctx.must_set_password && !res.ctx.notice_required && key.split(',').includes(res.ctx.active_role)) {
          setCtx(res.ctx);
        } else {
          navigate(step, { replace: true });
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, key]);

  return { ctx, error };
}
